import { Chains, Mode, Transport } from 'porto'
import type { Porto } from 'porto/remote'
import { http, type ValueOf } from 'viem'
import {
  arbitrum,
  arbitrumSepolia,
  base,
  baseSepolia,
  berachain,
  berachainBepolia,
  bsc,
  celo,
  gnosis,
  hoodi,
  katana,
  mainnet,
  optimism,
  optimismSepolia,
  polygon,
  sepolia,
} from 'viem/chains'

import * as Env from './Env'

const mock = import.meta.env.MODE === 'test'

const config = {
  anvil: {
    chains: [Chains.anvil, Chains.anvil2, Chains.anvil3],
    mode: Mode.relay({
      mock,
      multichain: false,
    }),
    relay: http(Transport.relayUrls.anvil.http),
  },
  prod: {
    // `chains` is generated by `scripts/chains.ts`
    chains: [
      arbitrum,
      arbitrumSepolia,
      base,
      baseSepolia,
      berachain,
      berachainBepolia,
      bsc,
      celo,
      gnosis,
      hoodi,
      katana,
      mainnet,
      optimism,
      optimismSepolia,
      polygon,
      sepolia,
    ],
    mode: Mode.relay({
      mock,
    }),
    relay: http(Transport.relayUrls.prod.http),
    // `transports` is generated by `scripts/chains.ts`
    transports: {
      [arbitrum.id]: http(import.meta.env.VITE_RPC_URL_ARBITRUM),
      [arbitrumSepolia.id]: http(),
      [base.id]: http(import.meta.env.VITE_RPC_URL_BASE),
      [baseSepolia.id]: http(),
      [berachain.id]: http(),
      [berachainBepolia.id]: http(),
      [bsc.id]: http(import.meta.env.VITE_RPC_URL_BSC),
      [celo.id]: http(),
      [gnosis.id]: http(),
      [hoodi.id]: http(),
      [katana.id]: http(),
      [mainnet.id]: http(),
      [optimism.id]: http(import.meta.env.VITE_RPC_URL_OPTIMISM),
      [optimismSepolia.id]: http(),
      [polygon.id]: http(import.meta.env.VITE_RPC_URL_POLYGON),
      [sepolia.id]: http(),
    },
  },
  stg: {
    // `chains` is generated by `scripts/chains.ts`
    chains: [
      arbitrum,
      arbitrumSepolia,
      base,
      baseSepolia,
      berachain,
      berachainBepolia,
      bsc,
      celo,
      gnosis,
      hoodi,
      katana,
      mainnet,
      optimism,
      optimismSepolia,
      polygon,
      sepolia,
    ],
    mode: Mode.relay({
      mock,
    }),
    relay: http(Transport.relayUrls.stg.http),
    storageKey: 'porto.store.stg',
    // `transports` is generated by `scripts/chains.ts`
    transports: {
      [arbitrum.id]: http(),
      [arbitrumSepolia.id]: http(),
      [base.id]: http(),
      [baseSepolia.id]: http(),
      [berachain.id]: http(),
      [berachainBepolia.id]: http(),
      [bsc.id]: http(),
      [celo.id]: http(),
      [gnosis.id]: http(),
      [hoodi.id]: http(),
      [katana.id]: http(),
      [mainnet.id]: http(),
      [optimism.id]: http(),
      [optimismSepolia.id]: http(),
      [polygon.id]: http(),
      [sepolia.id]: http(),
    },
  },
} as const satisfies Record<Env.Env, Partial<Porto.Config>>

const dialogHosts = {
  anvil: import.meta.env.PROD
    ? undefined
    : 'https://anvil.localhost:5174/dialog/',
  prod: import.meta.env.PROD
    ? 'https://id.porto.sh/dialog/'
    : 'https://localhost:5174/dialog/',
  stg: import.meta.env.PROD
    ? 'https://stg.id.porto.sh/dialog/'
    : 'https://stg.localhost:5174/dialog/',
} as const satisfies Record<Env.Env, string | undefined>

export function getConfig(
  env = Env.get(),
): Porto.Config<readonly [Chain, ...Chain[]]> {
  return config[env] as never
}

export function getDialogHost(env = Env.get()): string {
  const url = (() => {
    if (import.meta.env.VITE_DIALOG_HOST)
      return import.meta.env.VITE_DIALOG_HOST
    if (
      import.meta.env.VITE_VERCEL_ENV === 'preview' &&
      import.meta.env.VITE_VERCEL_BRANCH_URL
    )
      return (
        'https://' +
        import.meta.env.VITE_VERCEL_BRANCH_URL.replace(
          /(.*)(-git.*)/,
          'dialogporto$2',
        ) +
        '/dialog/'
      )
    return dialogHosts[env]
  })()
  return url + '?relayEnv=' + env
}

export type Chain = ValueOf<typeof config>['chains'][number]
export type ChainId = Chain['id']
